import {Injectable} from '@angular/core';
import {Subscription} from 'rxjs';
import {getFocusedScroller, isEditableElement, ListenerGroup} from './shared/keyboard-viewport.util';
import {MmKeyboardStateService} from './keyboard-state.service';

const REVEAL_MARGIN_PX = 12;

// Keeps the focused field visible across keyboard transitions. On open the scroller shrinks from
// the bottom, so a low field can end up hidden behind the keyboard; on close some dialogs swap
// their scroller element (writing dialog) and lose the scroll position. Scrolls only the field's
// own scroller, and only when the field is actually out of view.
@Injectable({providedIn: 'root'})
export class MmKeyboardFocusRevealService {

  private listeners = new ListenerGroup();
  private stateSub: Subscription | null = null;
  private lastEditable: HTMLElement | null = null;

  constructor(private keyboardState: MmKeyboardStateService) {}

  start(): void {
    if (this.stateSub || typeof document === 'undefined') {
      return;
    }
    this.listeners.add(document, 'focusin', event => {
      if (event.target instanceof HTMLElement && isEditableElement(event.target)) {
        this.lastEditable = event.target;
      }
    }, {capture: true});
    this.stateSub = this.keyboardState.keyboardOpen$.subscribe(open => {
      requestAnimationFrame(() => this.reveal(open ? document.activeElement as HTMLElement | null : this.lastEditable));
    });
  }

  revealField(field: HTMLElement | null): void {
    this.reveal(field);
  }

  private reveal(field: HTMLElement | null): void {
    if (!field || !field.isConnected || !isEditableElement(field)) {
      return;
    }
    const scroller = getFocusedScroller(field);
    if (scroller === document.documentElement || scroller === document.scrollingElement || scroller === document.body) {
      return;
    }
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }
    const el = field.getBoundingClientRect();
    const sc = scroller.getBoundingClientRect();
    const bandTop = Math.max(sc.top, 0);
    const bandBottom = Math.min(sc.bottom, vv.height);
    if (el.height <= bandBottom - bandTop) {
      // fits the band: fully visible = zero touch, otherwise the minimal nudge
      if (el.bottom > bandBottom) {
        scroller.scrollTop += el.bottom - bandBottom + REVEAL_MARGIN_PX;
      } else if (el.top < bandTop) {
        scroller.scrollTop += el.top - bandTop - REVEAL_MARGIN_PX;
      }
    } else if (el.top >= bandBottom || el.bottom <= bandTop) {
      // taller than the band (grown textareas): only when fully out, align its top
      scroller.scrollTop += el.top - bandTop - REVEAL_MARGIN_PX;
    }
  }
}
